/**
 * client/src/components/layout/security-section-tabs.tsx — Security section nav.
 *
 * Consolidates the four personal identity surfaces — Security Overview (the
 * protection posture summary), Security Keys (passkey/authenticator
 * management, split out from Security Overview per owner direction, 2026-08),
 * Memories (sensory-seed management), and Guardians (the recovery circle) —
 * under a single "Security" sidebar entry, surfaced as a segmented bar above
 * the active section.
 *
 * This is route NAVIGATION, not in-page tabs: each entry is a `<Link>` to its
 * own route (`/security-centre`, `/security-keys`, `/memories`,
 * `/guardian-setup`) and the active entry is derived from the path. Modelling
 * it as nav (not a `role="tablist"`) is deliberate — clicking unmounts/
 * remounts a whole route, supports cmd/ctrl-click "open in new tab", and
 * keeps THIS bar out of the accessibility tree as a tablist even though
 * Guardians owns a REAL same-page tablist of its own — My-circle/Protecting
 * (guardian-setup/index.tsx). Path-based selection on this outer bar means it
 * never contends with that page's own `?tab=` query param.
 *
 * Org context: these are personal-only surfaces (the org sidebar omits them and
 * the Security Keys/Memories/Guardians routes are `personal-only` — a click
 * would bounce). So in org context the bar is not rendered; only the section
 * content is.
 *
 * Colour (CLAUDE.md): emerald is reserved for success / hardware-confirmed /
 * AAL-milestone states (AC-57) — this is plain route navigation, not one of
 * those, so the active entry's "selected section" signal uses the neutral
 * interactive token instead. No red anywhere.
 */

import type { ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Shield, KeyRound, Brain, LifeBuoy, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import { useOrgContext } from "@/lib/use-org-context";
import { useRecoveryOfferings } from "@/lib/use-recovery-options";
import { useSecurityFeatures } from "@/lib/use-security-features";

type SectionId = "security" | "keys" | "recovery" | "memories" | "guardians";

const SECTIONS: ReadonlyArray<{ id: SectionId; label: string; href: string; Icon: typeof Shield }> = [
  { id: "security", label: "Security Overview", href: "/security-centre", Icon: Shield },
  { id: "keys", label: "Security Keys", href: "/security-keys", Icon: KeyRound },
  { id: "recovery", label: "Recovery", href: "/security-recovery", Icon: LifeBuoy },
  { id: "memories", label: "Memories", href: "/memories", Icon: Brain },
  { id: "guardians", label: "Guardians", href: "/guardian-setup", Icon: Users },
];

/** Map a runtime path to its section. Defaults to Security (the section home). */
export function sectionFromPath(path: string): SectionId {
  if (path.startsWith("/security-keys")) return "keys";
  if (path.startsWith("/security-recovery")) return "recovery";
  if (path.startsWith("/memories")) return "memories";
  if (path.startsWith("/guardian-setup")) return "guardians";
  return "security";
}

export function SecuritySectionTabs({ children }: { children: ReactNode }): JSX.Element {
  const [location] = useLocation();
  const { currentContext } = useOrgContext();
  // (ledger #62) A disabled security-model element loses its section entry —
  // linking to a surface whose API is 404 would be a dead control. The server
  // gates remain authoritative; this only stops the navigation offer.
  const { memoriesEnabled, guardiansEnabled } = useSecurityFeatures();
  // (AC-52) Recovery is gated on the SAME server facts that populate the page.
  // The server answers "is anything offerable?" once, in loadRecoveryOptions;
  // re-deriving it here from the method list is exactly how the tab and the
  // page drifted into offering a route to an empty page. Fails closed —
  // unloaded offers nothing.
  const { offerable: recoveryOfferable } = useRecoveryOfferings();

  // Personal-only surfaces — not offered as a group in org context (see header).
  if (currentContext === "org") return <>{children}</>;

  const sections = SECTIONS.filter(
    (s) =>
      (s.id !== "memories" || memoriesEnabled) &&
      (s.id !== "guardians" || guardiansEnabled) &&
      (s.id !== "recovery" || recoveryOfferable),
  );
  const active = sectionFromPath(location);

  return (
    <div>
      <nav aria-label="Security sections" className="mx-auto max-w-[760px] px-4 pt-2 pb-1">
        {/* Outlined buttons (not a filled segmented strip): each carries a
            visible border so it clearly reads as a clickable tab. Active = the
            neutral interactive outline + tint (the "selected section" signal);
            inactive = neutral outline that warms to interactive on hover.
            Emerald is reserved for success/hardware-confirmed/AAL-milestone
            states (AC-57), not plain navigation. */}
        <div className="flex flex-wrap gap-2">
          {sections.map((s) => {
            const isActive = s.id === active;
            return (
              <Link
                key={s.id}
                href={s.href}
                aria-current={isActive ? "page" : undefined}
                data-testid={`security-section-${s.id}`}
                className={cn(
                  "flex min-w-0 flex-1 basis-[45%] items-center justify-center gap-1.5 max-[320px]:flex-col sm:basis-0",
                  "min-h-[44px] rounded-lg border px-2 py-2 text-xs leading-normal font-medium sm:px-3",
                  "transition-colors duration-[var(--duration-fast)] motion-reduce:transition-none",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-interactive",
                  isActive
                    ? "border-interactive/40 bg-interactive/10 text-interactive shadow-[shadow:var(--elevation-1)]"
                    : "border-border-default text-text-tertiary hover:text-text-secondary hover:border-interactive/30 hover:bg-bg-overlay",
                )}
              >
                <s.Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0">{s.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
      {children}
    </div>
  );
}
